/**
 * Screen-2 copy — Lane 625 (Phase 5, the D7 confirm).
 *
 * Every customer-facing string on the confirm surface that is NOT computed
 * from a server response. (Those live in `noticeAttachStatus.js`, which
 * composes them from the counts the endpoint returns.)
 *
 * WHY A SEPARATE, PURE, DOM-FREE MODULE
 * -------------------------------------
 * The render harness Ryan reviews must be able to EXECUTE these rather than
 * transcribe them. `NoticeSupplierConfirm.js` imports React and returns JSX, so
 * node cannot load it; this module has no imports at all and runs anywhere.
 *
 * That is not a convenience. Lane 616's artifact claimed "every string is
 * produced by running the shipped module" while one state was transcribed from
 * `Home.js` — the claim was false, and it was caught by accident. Putting the
 * strings somewhere the harness can run them makes the claim structurally true
 * instead of maintained by care.
 */

/** The picker's static labels. */
export const PICKER_COPY = Object.freeze({
  SEARCH_PLACEHOLDER: 'Search suppliers by name…',
  SEARCH_LABEL: 'Search suppliers by name',
  SEARCHING: 'Searching…',
  NOT_ON_FILE_NAME: 'Not from a supplier on file',
  NOT_ON_FILE_HINT: 'Record that, and attribute nothing',
  PICKED_NOT_ON_FILE: 'Not a supplier on file',
  PICKED_NOT_ON_FILE_HINT: 'Wingman will record that, and attribute nothing.',
  CONFIRM: 'Confirm',
  CONFIRMING: 'Recording…',
  CHANGE: 'Change',
});

/**
 * Card copy that belongs to the confirm but is rendered by `Home.js` rather
 * than by an outcome object.
 */
export const CONFIRM_COPY = Object.freeze({
  /** `awaiting_supplier` with no candidate id — nothing to attach to. */
  NO_CANDIDATE:
    'Wingman cannot record who sent this one. It did not come back with a reference to ' +
    'the stored letter. Upload it again to retry.',
  /** Offered after a confirm that attributed nothing; the row stays attachable. */
  REPICK: 'Choose a supplier',
  /** Offered after a 404 — the stored letter is unreachable, so start fresh. */
  REUPLOAD: 'Upload it again',
});

/** `$4.2M` / `$883K` / `$0` — the chat picker's `formatSpend`, verbatim. */
function formatSpend(value) {
  const n = Number(value) || 0;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `$${Math.round(n / 1e3)}K`;
  return `$${Math.round(n)}`;
}

/**
 * The disambiguating hint — `components/SupplierDisambiguation.js`'s
 * `formatHint`, verbatim. Always a positive fact even at zero, so an absent
 * value never reads as a silent omission: the seven same-prefix Parker
 * divisions are unpickable by name alone.
 */
export function formatHint(l12mSpend, materialCount) {
  const count = Number(materialCount) || 0;
  return `${formatSpend(l12mSpend)} L12M · ${count} material${count === 1 ? '' : 's'}`;
}

/**
 * The search-outage line. Deliberately NOT the no-match line: "nothing matches"
 * is the buyer's cue to record this letter as coming from nobody on file, and a
 * search outage must not be able to walk them into that.
 */
export function searchFailedSentence(query) {
  return (
    'Supplier search is unavailable right now, so Wingman cannot tell you whether ' +
    `“${query}” is on file. Try again in a moment.`
  );
}

/** The genuine empty result. */
export function noMatchSentence(query) {
  return `No supplier on file matches “${query}”.`;
}

/**
 * What pressing Confirm will actually do. The zero and not-on-file cases say
 * plainly that nothing (or only the sender) gets recorded, rather than letting
 * a buyer confirm in the belief that the letter's contents are being captured.
 *
 * "claims" is the store's word, never the buyer's (D31(a)) — `claimsAvailable`
 * stays the parameter name because that is the field the endpoint returns, but
 * what renders names the provenance: "3 things this letter told us".
 */
export function confirmConsequence(notOnFile, claimsAvailable) {
  if (notOnFile) {
    return 'Nothing from the letter will be recorded. You can attach a supplier later.';
  }
  // ── A5a (Lane 676): UNKNOWN is not the same as KNOWN-ZERO ──────────────
  // These two collapsed before, and the collapse became visible the moment the
  // picker was offered from a DUPLICATE card: that response body carries no
  // `notice` object, so the count is genuinely unavailable — while the row it
  // describes reached `awaiting_supplier` THROUGH a successful extraction and
  // very probably does hold claims. Rendering the known-zero sentence there
  // would tell a buyer "there is nothing else in the letter to record" about a
  // letter we had simply not counted. That is an assertion we cannot support.
  //
  // `null` is already the unknown sentinel on the sibling branch in
  // `docUploadStatus.awaitingDetail`, which distinguishes the two cases; this
  // aligns the second module with the first rather than inventing a convention.
  if (claimsAvailable === null || claimsAvailable === undefined) {
    return 'Confirming records who sent it, and anything the letter told us.';
  }
  const n = Number(claimsAvailable);
  if (!Number.isFinite(n) || n <= 0) {
    return 'Confirming records who sent it. There is nothing else in the letter to record.';
  }
  const noun = n === 1 ? 'thing' : 'things';
  return `Confirming records ${n} ${noun} this letter told us against this supplier.`;
}
